import * as React from "react";
import { useMemo } from "react";
import {
  Calendar,
  CandlestickChart,
  Code2,
  FileCode2,
  Layers,
  Loader2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { PolicyKindChips } from "@/components/Policy/PolicyKindChips";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  REPORTS_SYMBOL_BADGE_CLASS,
  REPORTS_TYPE_BADGE_CLASS,
  ReportsMetaIconChip,
  getReportTypeBadgeClass,
  getReportTypeBadgeDataType,
} from "../Reports/components/ReportsCardChrome";
import {
  cnSimulatorMetricsDelta,
  SIMULATOR_METRICS_GRAY,
} from "../ExecutionRulesSimulator/simulatorMetricsTypography";
import { formatReportCardCurrency } from "../../../utils/reportListCardPreview";
import type { WorkspaceNodeMetaSourcePolicy, WorkspaceNodeType } from "../../../types/workspace";
import { formatWorkspaceHubDate } from "./WorkspaceHubLinkCell";
import {
  formatSimulationDeltaValue,
  formatWorkspaceNodeCardTitle,
  type WorkspaceEntityCardDetail,
} from "./workspaceEntityCardDetails";
import { WORKSPACE_NODE_TYPE_LABELS } from "./workspaceGraphUtils";
import { WorkspaceNodeIcon } from "./nodes/workspaceNodeIcon";
import { useTradePolicyFilterCatalogFields } from "@/hooks/useTradePolicyFilterCatalogFields";
import { buildPolicyViewNaturalLanguageSummary } from "../ExecutionRulesSimulator/policyTreeUtils";
import { PolicyViewSummaryContent } from "../Strategies/PolicyViewSummaryContent";

const META_LABEL_CLASS =
  "shrink-0 font-[family-name:var(--font-primary)] text-[10px] font-medium text-[color:var(--brand-tropical-surf)]";

const CARD_SHELL_CLASS = "flex h-full min-h-0 flex-col p-3";

/** Hub-parity header badges — fixed height, no vertical padding bleed. */
const WORKSPACE_NODE_HEADER_BADGE_CLASS =
  "inline-flex h-5 min-h-5 shrink-0 items-center justify-center gap-1 rounded-[2px] px-1.5 py-0 text-[10px] font-medium leading-none";

const CARD_MAIN_CLASS = "mt-2 flex min-h-0 flex-1 flex-col";

const CARD_BODY_SLOT_CLASS = "min-h-0 flex-1 overflow-hidden";

const CARD_BODY_CLASS =
  "line-clamp-2 font-[family-name:var(--font-primary)] text-[11px] leading-snug text-gray-500 dark:text-sidebar-foreground";

const CARD_DATE_CLASS =
  "font-[family-name:var(--font-primary)] text-[10px] text-gray-500 dark:text-sidebar-foreground";

const CARD_FOOTER_CLASS =
  "shrink-0 flex items-end justify-between gap-1.5 border-t border-border pt-2";

const CARD_TITLE_CLASS =
  "font-[family-name:var(--font-primary)] text-[13px] font-medium leading-5 text-[color:var(--brand-tropical-surf)] dark:text-white";

const TYPE_LABEL_CLASS =
  "font-[family-name:var(--font-primary)] text-[9px] font-medium uppercase tracking-wide text-[color:var(--brand-tropical-surf)]";

/** Shared inset between the icon box and text column. */
const CARD_HEADER_GAP_CLASS = "gap-2.5";

function SimulationMetric({
  label,
  value,
  delta,
  toneDelta = true,
  hint,
}: {
  label: string;
  value: string;
  delta: number | null;
  toneDelta?: boolean;
  hint?: string;
}): React.ReactElement {
  return (
    <div className="min-w-0 flex-1 text-left">
      <p className={META_LABEL_CLASS}>{label}</p>
      <p
        className={cn(
          "truncate font-[family-name:var(--font-primary)] text-[11px] font-medium tabular-nums leading-snug",
          toneDelta ? cnSimulatorMetricsDelta(delta, "") : SIMULATOR_METRICS_GRAY
        )}
      >
        {value}
      </p>
      {hint ? (
        <p className="truncate font-[family-name:var(--font-primary)] text-[9px] text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function CandleTimeframeFooterChip({
  timeframe,
  timeframes,
}: {
  timeframe: string;
  timeframes?: string[];
}): React.ReactElement | null {
  if (!timeframe || timeframe === "—") return null;
  const multi = (timeframes?.length ?? 0) > 1;
  return (
    <ReportsMetaIconChip
      id="timeframe"
      label={multi ? "Timeframes" : "Timeframe"}
      value={timeframe}
      icon={CandlestickChart}
    />
  );
}

function EntityCardDateFooter({
  label,
  date,
  trailing,
}: {
  label: string;
  date?: string;
  trailing?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className={CARD_FOOTER_CLASS}>
      <div className="min-w-0">
        <p className={META_LABEL_CLASS}>{label}</p>
        <p className={CARD_DATE_CLASS}>{formatWorkspaceHubDate(date)}</p>
      </div>
      {trailing ? <div className="flex shrink-0 items-end">{trailing}</div> : null}
    </div>
  );
}

function StrategyCardBody({ detail }: { detail: Extract<WorkspaceEntityCardDetail, { kind: "strategy" }> }): React.ReactElement {
  return <p className={CARD_BODY_CLASS}>{detail.description}</p>;
}

function ReportCardBody({
  detail,
  dismissing,
}: {
  detail: Extract<WorkspaceEntityCardDetail, { kind: "report" }>;
  dismissing?: boolean;
}): React.ReactElement {
  const pnlColor =
    detail.totalPnl == null
      ? undefined
      : detail.totalPnl >= 0
        ? "var(--tessera-pnl-positive)"
        : "#E00101";

  const statsLine = detail.processing ? (
    dismissing ? (
      "Generation failed"
    ) : (
      "Generating report…"
    )
  ) : detail.totalPnl != null && detail.totalTrades != null ? (
    <>
      <span data-trade-log-pnl="" className="font-medium" style={{ color: pnlColor }}>
        {formatReportCardCurrency(detail.totalPnl)}
      </span>
      {" · "}
      {detail.totalTrades} trades ·{" "}
      {detail.winRate != null && Number.isFinite(detail.winRate)
        ? `${detail.winRate.toFixed(1)}% win rate`
        : "— win rate"}
    </>
  ) : (
    "No summary yet"
  );

  return <p className={CARD_BODY_CLASS}>{statsLine}</p>;
}

function SimulationCardBody({
  detail,
}: {
  detail: Extract<WorkspaceEntityCardDetail, { kind: "simulation" }>;
}): React.ReactElement {
  const hasSummary =
    detail.pnlDelta != null ||
    detail.winRateDelta != null ||
    detail.tradesDelta != null ||
    detail.tradesChanged != null ||
    detail.tradesRemoved != null;

  if (!hasSummary) {
    return <p className={CARD_BODY_CLASS}>No summary yet</p>;
  }

  const showHybridRemoved =
    detail.tradesChanged != null &&
    detail.tradesRemoved != null &&
    detail.tradesRemoved !== 0 &&
    detail.tradesRemoved !== detail.tradesChanged;

  return (
    <div className="flex min-w-0 items-start gap-1.5">
      <SimulationMetric
        label="PnL Change"
        delta={detail.pnlDelta}
        value={formatSimulationDeltaValue(detail.pnlDelta, "pnl")}
      />
      <SimulationMetric
        label="Win rate"
        delta={detail.winRateDelta}
        value={formatSimulationDeltaValue(detail.winRateDelta, "winRate")}
      />
      <SimulationMetric
        label={detail.tradesDeltaLabel || "Trades"}
        delta={detail.tradesDelta}
        toneDelta={false}
        value={formatSimulationDeltaValue(detail.tradesDelta, "trades")}
        hint={
          showHybridRemoved
            ? `${formatSimulationDeltaValue(detail.tradesRemoved, "trades")} removed`
            : undefined
        }
      />
    </div>
  );
}

function formatSourcePolicyLine(source: WorkspaceNodeMetaSourcePolicy): string {
  const account = source.brokerAccountCode?.trim() || "Unknown account";
  const version =
    source.versionNumber != null && Number.isFinite(source.versionNumber)
      ? `v${source.versionNumber}`
      : null;
  const title = source.title?.trim() || null;
  const bits = [account, version, title].filter(Boolean);
  return bits.join(" · ");
}

function CrossAccountCombinedBadge({
  sources,
}: {
  sources: WorkspaceNodeMetaSourcePolicy[];
}): React.ReactElement {
  const badge = (
    <Badge
      variant="outline"
      data-workspace-cross-account-combined=""
      className={cn(WORKSPACE_NODE_HEADER_BADGE_CLASS, REPORTS_TYPE_BADGE_CLASS)}
    >
      Cross-account
    </Badge>
  );

  if (sources.length === 0) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          className="max-w-xs flex-col items-stretch gap-1 bg-popover px-2.5 py-2 text-popover-foreground"
          arrowClassName="bg-popover fill-popover"
        >
          <p className="m-0 font-[family-name:var(--font-primary)] text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Source policies
          </p>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {sources.map((source) => {
              const onCanvas = source.onThisCanvas !== false;
              return (
                <li
                  key={source.policyVersionId}
                  className={cn(
                    "m-0 font-[family-name:var(--font-primary)] text-[11px] leading-snug",
                    onCanvas ? "text-popover-foreground" : "text-muted-foreground opacity-70"
                  )}
                  title={
                    onCanvas
                      ? undefined
                      : "Source policy is not on this canvas"
                  }
                >
                  {formatSourcePolicyLine(source)}
                  {!onCanvas ? " · off-canvas" : ""}
                </li>
              );
            })}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PolicyCardBody({
  detail,
}: {
  detail: Extract<WorkspaceEntityCardDetail, { kind: "policy" }>;
}): React.ReactElement {
  // Canvas cards only have meta (policyTree is null). Skip catalog until a
  // drawer/enrichment path supplies a tree — otherwise every policy node
  // mounts its own filter-catalog GET.
  const catalog = useTradePolicyFilterCatalogFields(Boolean(detail.policyTree));
  const summaryDocument = useMemo(() => {
    if (!detail.policyTree || catalog.catalogLoading || catalog.catalogError) return null;
    try {
      return buildPolicyViewNaturalLanguageSummary(detail.policyTree, catalog.fieldsByPath);
    } catch {
      return null;
    }
  }, [detail.policyTree, catalog.catalogLoading, catalog.catalogError, catalog.fieldsByPath]);

  if (summaryDocument) {
    return (
      <PolicyViewSummaryContent
        document={summaryDocument}
        className="gap-1"
        paragraphClassName="text-[11px] leading-snug line-clamp-3"
      />
    );
  }

  const versionLabel =
    detail.versionNumber != null && Number.isFinite(detail.versionNumber)
      ? `v${detail.versionNumber}`
      : null;

  if (
    detail.accounts.length > 0 ||
    versionLabel ||
    detail.isCombined ||
    detail.isCrossAccountCombined ||
    detail.sourcePolicies.length > 0 ||
    detail.policyKinds.length > 0
  ) {
    return (
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {detail.isCrossAccountCombined ? (
            <CrossAccountCombinedBadge sources={detail.sourcePolicies} />
          ) : detail.isCombined ? (
            <Badge
              variant="outline"
              className={cn(WORKSPACE_NODE_HEADER_BADGE_CLASS, REPORTS_TYPE_BADGE_CLASS)}
            >
              Combined
            </Badge>
          ) : null}
          {versionLabel ? (
            <Badge
              variant="outline"
              className={cn(WORKSPACE_NODE_HEADER_BADGE_CLASS, REPORTS_SYMBOL_BADGE_CLASS)}
            >
              {versionLabel}
            </Badge>
          ) : null}
          <PolicyKindChips kinds={detail.policyKinds} />
        </div>
        {detail.isCrossAccountCombined && detail.sourcePolicies.length > 0 ? (
          <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
            {detail.sourcePolicies.map((source) => {
              const onCanvas = source.onThisCanvas !== false;
              const account = source.brokerAccountCode?.trim() || "Unknown account";
              return (
                <li
                  key={source.policyVersionId}
                  className={cn(
                    "m-0 truncate font-[family-name:var(--font-primary)] text-[10px] leading-snug",
                    onCanvas
                      ? "text-gray-500 dark:text-sidebar-foreground"
                      : "text-muted-foreground opacity-60"
                  )}
                  title={formatSourcePolicyLine(source)}
                >
                  {account}
                  {!onCanvas ? " · off-canvas" : ""}
                </li>
              );
            })}
          </ul>
        ) : detail.accounts.length > 0 ? (
          <div className="flex min-w-0 flex-wrap gap-1">
            {detail.accounts.map((account) => (
              <Badge
                key={`${account.brokerName}:${account.brokerAccountCode}`}
                variant="outline"
                className={cn(WORKSPACE_NODE_HEADER_BADGE_CLASS, REPORTS_SYMBOL_BADGE_CLASS)}
                title={`Executing: ${account.brokerAccountCode}`}
              >
                {account.brokerAccountCode}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <p className="font-[family-name:var(--font-primary)] text-[11px] leading-snug text-gray-500">
      {catalog.catalogLoading && detail.policyTree ? "Loading…" : "No summary yet"}
    </p>
  );
}

function GenericPipelineCardBody({
  subtitle,
}: {
  subtitle: string;
}): React.ReactElement | null {
  if (!subtitle) return null;
  return <p className={CARD_BODY_CLASS}>{subtitle}</p>;
}

export function WorkspaceEntityCardContent({
  nodeType,
  typeLabel,
  title,
  subtitle,
  detail,
  isRerun,
  isFork,
  isReportGenerating,
  isSimulationRunning,
  dismissing,
  combineSelectionAnchor,
  combinedSourceHighlight,
}: {
  nodeType: WorkspaceNodeType;
  typeLabel: string;
  title: string;
  subtitle: string;
  detail?: WorkspaceEntityCardDetail | null;
  isRerun?: boolean;
  isFork?: boolean;
  isReportGenerating?: boolean;
  isSimulationRunning?: boolean;
  dismissing?: boolean;
  combineSelectionAnchor?: boolean;
  combinedSourceHighlight?: boolean;
}): React.ReactElement {
  const typeLabelDisplay = isRerun
    ? nodeType === "combined_simulation"
      ? "Combined simulation rerun"
      : "Simulation rerun"
    : isFork
      ? nodeType === "combined_policy"
        ? "Combined policy fork"
        : "Policy fork"
      : typeLabel;

  const processingBadge =
    isReportGenerating || isSimulationRunning ? (
      <span
        data-workspace-node-card-badge=""
        data-workspace-node-processing-badge=""
        className={cn(
          WORKSPACE_NODE_HEADER_BADGE_CLASS,
          "border border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-200"
        )}
      >
        {!dismissing && (isReportGenerating || isSimulationRunning) ? (
          <Loader2 className="size-3 shrink-0 animate-spin text-[#38BBD1]" aria-hidden />
        ) : null}
        {dismissing ? "Failed" : isReportGenerating ? "Generating…" : "Running…"}
      </span>
    ) : null;

  const reportTypeBadge =
    detail?.kind === "report" && !detail.processing ? (
      <Badge
        data-workspace-node-card-badge=""
        data-reports-type-badge=""
        data-report-type={getReportTypeBadgeDataType(detail.reportTypeKey || detail.reportType)}
        className={cn(
          WORKSPACE_NODE_HEADER_BADGE_CLASS,
          getReportTypeBadgeClass(detail.reportTypeKey || detail.reportType)
        )}
        title={detail.reportType}
      >
        {detail.reportType}
      </Badge>
    ) : null;

  const footerBadge = reportTypeBadge;

  let headerBadge: React.ReactNode = footerBadge;
  let footerMeta: React.ReactNode = null;
  let body: React.ReactNode = null;
  let footer: React.ReactNode = null;

  if (detail?.kind === "strategy") {
    const policyCount = detail.policyVersionCount;
    footerMeta = (
      <div className="flex shrink-0 items-center gap-1">
        <ReportsMetaIconChip
          id="policies"
          label="Policy versions"
          value={policyCount > 0 ? String(policyCount) : "None"}
          icon={Layers}
        />
        <ReportsMetaIconChip
          id="master-pine"
          label="Master Pinescript"
          value={detail.masterPineLabel}
          icon={FileCode2}
        />
        <ReportsMetaIconChip
          id="active-pine"
          label="Active Pinescript"
          value={detail.activePineLabel}
          icon={Code2}
        />
      </div>
    );
    headerBadge = null;
    body = <StrategyCardBody detail={detail} />;
    footer = (
      <EntityCardDateFooter label="Updated" date={detail.updatedAt} trailing={footerMeta} />
    );
  } else if (detail?.kind === "report") {
    if (detail.processing || dismissing) {
      headerBadge = processingBadge;
    } else {
      footerMeta = (
        <div className="flex shrink-0 items-center gap-1">
          <ReportsMetaIconChip
            id="window"
            label="Window"
            value={detail.windowLabel}
            icon={Calendar}
          />
          <ReportsMetaIconChip
            id="timeframe"
            label="Timeframe"
            value={detail.timeframe}
            icon={CandlestickChart}
          />
        </div>
      );
    }
    body = <ReportCardBody detail={detail} dismissing={dismissing} />;
    footer = (
      <EntityCardDateFooter label="Created" date={detail.createdAt} trailing={footerMeta} />
    );
  } else if (detail?.kind === "simulation") {
    if (isSimulationRunning || dismissing) {
      headerBadge = processingBadge;
    } else {
      footerMeta = (
        <div className="flex shrink-0 items-center gap-1">
          <ReportsMetaIconChip
            id="window"
            label="Window"
            value={detail.windowLabel}
            icon={Calendar}
          />
          <ReportsMetaIconChip
            id="timeframe"
            label={
              (detail.timeframes?.length ?? 0) > 1 ? "Timeframes" : "Timeframe"
            }
            value={detail.timeframe}
            icon={CandlestickChart}
          />
        </div>
      );
    }
    body = <SimulationCardBody detail={detail} />;
    footer = (
      <EntityCardDateFooter label="Created" date={detail.createdAt} trailing={footerMeta} />
    );
  } else if (detail?.kind === "policy") {
    body = <PolicyCardBody detail={detail} />;
    footerMeta = (
      <CandleTimeframeFooterChip
        timeframe={detail.timeframe}
        timeframes={detail.timeframes}
      />
    );
    footer = (
      <EntityCardDateFooter label="Created" date={detail.createdAt} trailing={footerMeta} />
    );
  } else if (combineSelectionAnchor) {
    body = (
      <p className="truncate font-[family-name:var(--font-primary)] text-[10px] leading-snug text-[color:var(--brand-tropical-surf)]">
        Adopts this report window
      </p>
    );
  } else if (combinedSourceHighlight) {
    body = (
      <p className="truncate font-[family-name:var(--font-primary)] text-[10px] leading-snug text-amber-600 dark:text-amber-300">
        Combined from this policy
      </p>
    );
  } else if (isReportGenerating || isSimulationRunning) {
    headerBadge = processingBadge;
    body = (
      <p className={CARD_BODY_CLASS}>
        {isReportGenerating ? "Generating report…" : "Running simulation…"}
      </p>
    );
  } else {
    body = <GenericPipelineCardBody subtitle={subtitle} />;
  }

  const displayTitle =
    detail?.kind === "strategy" ? detail.title : title;
  const trimmedDisplayTitle = displayTitle.trim();
  const renderedTitle = formatWorkspaceNodeCardTitle(trimmedDisplayTitle);

  return (
    <div className={CARD_SHELL_CLASS}>
      <div
        data-workspace-node-card-header=""
        className={cn("relative flex shrink-0 items-start", CARD_HEADER_GAP_CLASS)}
      >
        <WorkspaceNodeIcon
          type={nodeType}
          variant="card"
          isRerun={isRerun}
          isFork={isFork}
          className="shrink-0"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p
            data-workspace-node-type-label=""
            className={cn(
              TYPE_LABEL_CLASS,
              "m-0 min-w-0 leading-none",
              headerBadge && "pr-24",
              isRerun &&
                (nodeType === "combined_simulation"
                  ? "text-emerald-800 dark:text-emerald-400"
                  : "text-green-800 dark:text-green-400"),
              isFork &&
                !isRerun &&
                (nodeType === "combined_policy"
                  ? "text-emerald-700 dark:text-emerald-300"
                  : "text-orange-700 dark:text-orange-300"),
              combinedSourceHighlight && "text-amber-600 dark:text-amber-300"
            )}
          >
            {typeLabelDisplay}
          </p>
          <p
            data-workspace-node-title=""
            className={cn(CARD_TITLE_CLASS, "m-0 min-w-0 whitespace-nowrap")}
            title={trimmedDisplayTitle}
          >
            {renderedTitle}
          </p>
        </div>
        {headerBadge ? (
          <div
            data-workspace-node-card-badge-slot=""
            className="absolute right-0 top-0 shrink-0"
          >
            {headerBadge}
          </div>
        ) : null}
      </div>

      <div className={CARD_MAIN_CLASS}>
        <div className={CARD_BODY_SLOT_CLASS}>{body}</div>
        {footer}
      </div>
    </div>
  );
}

export function workspaceEntityCardTypeLabel(nodeType: WorkspaceNodeType): string {
  return WORKSPACE_NODE_TYPE_LABELS[nodeType] ?? nodeType;
}
